import { useMemo } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  Badge,
  CopyPlusIcon,
  Link,
  Notice,
  Tooltip,
  TrendingUpIcon,
} from '@buffer-mono/popcorn'

import { useWeeklyPostingStreak } from '~publish/components/MainSidebar/components/Streaks/hooks'
import { getStreakTopPercentLabel } from '~publish/components/MainSidebar/components/Streaks/streakPercentile'
import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { getZonedNow, isMonday } from '~publish/helpers/temporal'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import {
  useAccount,
  useAccountTimezone,
  useChannelsConnected,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { useCommentsChannels } from '~publish/components/CommentsSidebar/useCommentsChannels'
import { useCommunityConfig } from '~publish/pages/Community/CommunityConfig'
import { useCommentsScore } from '~publish/pages/Community/hooks/scoring/useCommentsScore'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { AnimatedNumber } from '../components/AnimatedNumber'
import { Card } from '../Card/Card'
import { usePostingGoalsSummary } from '../usePostingGoalsSummary'

import {
  getCommentScoreCopy,
  getPostingGoalsCopy,
  getStreakCopy,
  resolveStreakState,
} from './scorecardCopy'
import type { CommentScoreCopy, StreakCopy } from './scorecardCopy'
import { CommentScoreHoverCard } from './CommentScoreHoverCard'
import { PostingGoalsHoverCard } from './PostingGoalsHoverCard'
import { StatItem } from './StatItem'
import { StreakHoverCard } from './StreakHoverCard'
import {
  getCommentScoreTileClass,
  getPostingGoalsTileClass,
  getStreakFill,
  getStreakTileClass,
} from './tileMappings'
import { useStableSeed } from './useStableSeed'

import styles from './ScorecardPanel.module.css'
import statItemStyles from './StatItem.module.css'
import tileStyles from './tiles.module.css'

// Posting Goals tile percentage of goals completed (guard total === 0).
function postingGoalsPercent(completed: number, total: number): number {
  if (total === 0) return 0
  return Math.round((completed / total) * 100)
}

const CONNECT_CHANNEL_CTA = 'publish-home-scorecard-connectChannel-1'
const STREAK_CTA = 'publish-home-scorecard-streak-1'

function StreakCtaLink({ children }: { children: string }): JSX.Element {
  return (
    <NewPostComposerTrigger cta={STREAK_CTA}>
      <Link
        as="button"
        type="button"
        color="inherit"
        className={styles.scorecardCtaLink}
      >
        {children}
      </Link>
    </NewPostComposerTrigger>
  )
}

function StreakSubtext({ copy }: { copy: StreakCopy }): JSX.Element {
  if (copy.kind === 'link') {
    return <StreakCtaLink>{copy.text}</StreakCtaLink>
  }
  if (copy.kind === 'prefixLink') {
    return (
      <>
        {copy.prefix} <StreakCtaLink>{copy.link}</StreakCtaLink>
      </>
    )
  }
  return <>{copy.text}</>
}

/**
 * "Top X%" percentile badge for the streak heading. Only top-ranked orgs get a
 * label at all — see {@link getStreakTopPercentLabel}.
 */
function StreakPercentileBadge({ label }: { label: string }): JSX.Element {
  return (
    <Tooltip content="Compared to other Buffer users">
      <Badge size="small" className={styles.streakBadge} tabIndex={0}>
        {label}
      </Badge>
    </Tooltip>
  )
}

function ConnectChannelNotice(): JSX.Element {
  const handleConnect = (): void => {
    window?.appshell?.actions?.openChannelStorefront({
      cta: CONNECT_CHANNEL_CTA,
    })
  }

  return (
    <Notice
      variant="info"
      icon={<CopyPlusIcon />}
      className={styles.emptyNotice}
      data-testid="scorecard-panel-empty-banner"
    >
      <Notice.Text>
        <Link
          as="button"
          type="button"
          color="inherit"
          onClick={handleConnect}
          className={styles.emptyNoticeLink}
        >
          Connect a channel
        </Link>{' '}
        to start tracking your posting streak, set goals and more.
      </Notice.Text>
    </Notice>
  )
}

function CommentScoreSubtext({
  copy,
}: {
  copy: CommentScoreCopy
}): JSX.Element {
  if (copy.kind === 'text') {
    return <>{copy.text}</>
  }
  const isUp = copy.sign === '+'
  return (
    <span className={statItemStyles.trend}>
      <TrendingUpIcon
        color={isUp ? 'success' : 'critical'}
        aria-hidden={false}
        aria-label={copy.sign}
        className={clsx(
          statItemStyles.trendIcon,
          !isUp && statItemStyles.trendIconFlipped,
        )}
      />
      {copy.text}
    </span>
  )
}

export function ScorecardPanel(): JSX.Element {
  const organizationId = useOrganizationId()
  const { loading: accountLoading } = useAccount()
  const channels = useChannelsConnected()
  const openGlobalModal = useOpenGlobalModal()
  const { channels: commentsChannels } = useCommentsChannels()
  const {
    config: { isSupportedInComments },
  } = useCommunityConfig()

  // Only request scores for channels the actor can view comments on.
  // Passing inaccessible IDs to the backend throws and cascades to the
  // "All channel score calculations failed" error path.
  const channelIds = useMemo(
    () =>
      commentsChannels
        .filter(
          (channel) =>
            isSupportedInComments(channel) &&
            canPerform(channel, 'viewComments'),
        )
        .map((channel) => channel.id),
    [commentsChannels, isSupportedInComments],
  )
  const postingGoalChannelIds = useMemo(
    () =>
      channels
        // The action already requires an unlocked channel, so it subsumes the
        // explicit lock check this used to carry.
        .filter((channel) => canPerform(channel, 'managePostingSchedule'))
        .map((channel) => channel.id),
    [channels],
  )

  const { data: streakData, loading: streakLoading } = useWeeklyPostingStreak(
    organizationId ?? undefined,
  )
  const streakCount = streakData?.weeklyPostingStreak?.count ?? 0
  const streakStatus = streakData?.weeklyPostingStreak?.status
  const streakPeriodsAhead = streakData?.weeklyPostingStreak?.periodsAhead ?? 0
  const streakPercentileRank = streakData?.weeklyPostingStreak?.percentileRank
  const streakTopPercentLabel = getStreakTopPercentLabel(streakPercentileRank)
  const streakSeed = useStableSeed()
  // The account timezone, not the browser's. Home and the sidebar popover
  // must name the same day for a user who travels, or whose profile zone
  // differs from the device.
  const accountTimezone = useAccountTimezone()
  const streakIsMonday = isMonday(getZonedNow(accountTimezone))
  const streakCopyState = resolveStreakState({
    status: streakStatus,
    count: streakCount,
    periodsAhead: streakPeriodsAhead,
  })

  const {
    goals,
    completed: goalsCompleted,
    total: goalsTotal,
    loading: goalsLoading,
    hasData: hasPostingGoalsData,
    refresh: refreshPostingGoals,
  } = usePostingGoalsSummary(organizationId ?? undefined)
  const goalsPercent = postingGoalsPercent(goalsCompleted, goalsTotal)

  const { commentsScore, loading: scoreLoading } = useCommentsScore({
    channelIds,
  })
  const scoreValue = commentsScore?.currentPeriodScore.totalValue ?? 0
  const previousScoreValue = commentsScore?.previousPeriodScore.totalValue ?? 0
  const scoreSeed = useStableSeed()

  const hasNoChannels = !accountLoading && channels.length === 0
  const loading =
    !hasNoChannels && (streakLoading || goalsLoading || scoreLoading)
  const canSetPostingGoals =
    hasPostingGoalsData && goalsTotal === 0 && postingGoalChannelIds.length > 0

  const handleSetPostingGoals = (): void => {
    openGlobalModal(MODALS.postingGoalConfiguration, {
      channelIds: postingGoalChannelIds,
      source: 'home-page',
      onPostingGoalUpdated: refreshPostingGoals,
    })
  }

  return (
    <article className={styles.panel}>
      <Card
        radius="lg"
        className={clsx(styles.row, hasNoChannels && styles.rowEmpty)}
      >
        <StatItem
          tileContent={
            hasNoChannels ? (
              0
            ) : (
              <AnimatedNumber
                value={streakCount}
                animateAsWhole
                animateOnStart
              />
            )
          }
          tileClassName={getStreakTileClass(streakStatus, tileStyles)}
          fill={
            hasNoChannels ? 0 : getStreakFill(streakStatus, streakPeriodsAhead)
          }
          loading={loading}
          label="Week Streak"
          popoverContent={<StreakHoverCard />}
          labelAccessory={
            hasNoChannels || streakTopPercentLabel == null ? null : (
              <StreakPercentileBadge label={streakTopPercentLabel} />
            )
          }
          value={
            hasNoChannels ? null : (
              <StreakSubtext
                copy={getStreakCopy(streakCopyState, streakSeed, {
                  isMonday: streakIsMonday,
                })}
              />
            )
          }
          data-testid="scorecard-panel-streak"
        />
        <StatItem
          tileContent={
            hasNoChannels ? (
              0
            ) : (
              <span
                className={clsx(
                  statItemStyles.tileValue,
                  goalsPercent === 100 && statItemStyles.tileValueCompact,
                )}
              >
                <AnimatedNumber
                  value={goalsPercent}
                  animateAsWhole
                  animateOnStart
                />
                {goalsPercent > 0 && (
                  <span className={statItemStyles.tileSuffix}>%</span>
                )}
              </span>
            )
          }
          tileClassName={getPostingGoalsTileClass(goalsPercent, tileStyles)}
          fill={hasNoChannels ? 0 : goalsPercent / 100}
          loading={loading}
          label="Posting Goals"
          popoverContent={<PostingGoalsHoverCard goals={goals} />}
          value={
            hasNoChannels ? null : canSetPostingGoals ? (
              <>
                No goals yet ·{' '}
                <Link
                  as="button"
                  type="button"
                  color="inherit"
                  onClick={handleSetPostingGoals}
                  className={styles.scorecardCtaLink}
                >
                  Set goals
                </Link>
              </>
            ) : (
              getPostingGoalsCopy(goalsCompleted, goalsTotal)
            )
          }
          data-testid="scorecard-panel-posting-goals"
        />
        <StatItem
          tileContent={
            hasNoChannels ? (
              0
            ) : (
              <AnimatedNumber
                value={scoreValue}
                animateAsWhole
                animateOnStart
              />
            )
          }
          tileClassName={getCommentScoreTileClass(scoreValue, tileStyles)}
          fill={hasNoChannels ? 0 : scoreValue / 100}
          loading={loading}
          label="Comment Score"
          popoverContent={
            commentsScore ? (
              <CommentScoreHoverCard score={commentsScore} />
            ) : (
              'Your average response rate, speed, and consistency replying to comments.'
            )
          }
          value={
            hasNoChannels ? null : (
              <CommentScoreSubtext
                copy={getCommentScoreCopy({
                  hasData: commentsScore !== null,
                  current: scoreValue,
                  previous: previousScoreValue,
                  seed: scoreSeed,
                })}
              />
            )
          }
          data-testid="scorecard-panel-comment-score"
        />
      </Card>
      {hasNoChannels && <ConnectChannelNotice />}
    </article>
  )
}
